import { useMemo, useState } from "react";

import { orderBy, sumBy } from "lodash";
import moment from "moment";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { formatKpiEquation } from "@features/mmm/components/model-insights/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFeatureFlags } from "@hooks/http";
import { type FetchHistoricalMixForCycle } from "@north-beam/nb-client-side";
import { dateRangeLength, shiftISODate } from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
// import { useAvailableBudgetMixOptions } from "@pages/mmm/hooks/use-available-budget-mix-options";
import { useClientDataColumns } from "@pages/mmm/hooks/use-client-data-columns";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { useSelectedCyclePreviousPeriod } from "@pages/mmm/hooks/use-selected-cycle-previous-period";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";

import { useSelectedCyclePriorPeriod } from "../../hooks/use-selected-cycle-prior-period";
import { useShowReferencePeriod } from "../../hooks/use-show-reference-period";
import { useUpsertClientSetting } from "../hooks/use-upsert-client-setting";
import { KpiType, type ClientSetting, type EmptyClientSetting } from "../types";

import { SettingBudgetsField } from "./setting-budgets-field";
import { SettingEfficiencyGoalsField } from "./setting-efficiency-goals-field";
import { SettingKpiField } from "./setting-kpi-field";
import { SettingNameField } from "./setting-name-field";
import { SettingReferencePeriodField } from "./setting-reference-period-field";
import { SettingSpendRequirementsField } from "./setting-spend-requirements-field";

export enum FormKpiType {
  SIMPLE,
  COMPLEX,
}

export type FormKpi = {
  type: FormKpiType;
  id: string;
  name: string;
  equation?: any | null;
};

export const SpendConstraintSchema = z
  .object({
    data_column_id: z.string({ required_error: "Required field." }),
    budget_low: z.string().nullable(),
    budget_high: z.string().nullable(),
  })
  .refine(
    (data) => {
      if (data.budget_low === null || data.budget_low === "") {
        return true;
      }

      if (data.budget_high === null || data.budget_high === "") {
        return true;
      }

      return (
        Number.parseFloat(data.budget_low) <=
        Number.parseFloat(data.budget_high)
      );
    },
    {
      message: "*less than minimum",
      path: ["budget_high"],
    },
  );

export const MixValidationSchema = z
  .object({
    data_column_id: z.string({ required_error: "Required field." }),
    percentage_low: z.number({ required_error: "Required field." }),
    percentage_high: z.number({ required_error: "Required field" }),
  })
  .refine(
    (data) => {
      return data.percentage_low + 5 <= data.percentage_high;
    },
    {
      message: "Difference between max and min must be more than 5%",
      path: ["percentage_low"],
    },
  );

export const ClientSettingSchema = z
  .object({
    name: z
      .string({ required_error: "Required field." })
      .nonempty({ message: "Required field." }),
    budget_low: z
      .string({ required_error: "Required field." })
      .nonempty({ message: "Required Field." }),
    budget_high: z.string({ required_error: "Required Field." }).nonempty({
      message: "Required Field.",
    }),
    target_spend: z.string({ required_error: "Required field." }).nonempty({
      message: "Required Field.",
    }),
    max_cost_per_unit_kpi: z.string().optional(),
    profit_value_per_unit_kpi: z.string().optional(),

    kpi: z.string(),
    desired_kpi_value: z.number().optional(),
    spend_constraints: z.array(SpendConstraintSchema),
    reference_start_date: z.string().optional(),
    reference_end_date: z.string().optional(),
    // mix_validations: z.array(MixValidationSchema),
  })
  .refine(
    (data) => {
      if (!data.budget_low || !data.budget_low) {
        return true;
      }

      if (data.spend_constraints.length === 0) {
        return true;
      }

      const budgetHigh = Number(data.budget_high);

      const sumOfSpendConstraintsLow = sumBy(
        data.spend_constraints,
        ({ budget_low }) => Number(budget_low),
      );
      const isSpendConstraintsLowValid = sumOfSpendConstraintsLow <= budgetHigh;

      return isSpendConstraintsLowValid;
    },
    {
      message: "*Sum of spend requirement mins must not exceed budget max",
      path: ["invalid_spend_constraints"],
    },
  )
  .refine(
    (data) => {
      const isInvalidValue = (val: any) =>
        val === null || val === undefined || val === "";
      let isValid = true;
      data.spend_constraints.forEach((constraint) => {
        if (
          isInvalidValue(constraint.budget_low) &&
          isInvalidValue(constraint.budget_high)
        ) {
          isValid = false;
        }
      });
      return isValid;
    },
    {
      message:
        "*Either a low or high spend requirement must be defined, or the spend requirement should be removed",
      path: ["invalid_spend_constraints"],
    },
  )
  .refine(
    (data) => {
      if (!data.budget_low || !data.budget_low) {
        return false;
      }
      const budgetLow = Number(data.budget_low);
      const budgetHigh = Number(data.budget_high);
      const targetSpend = Number(data.target_spend);

      if (targetSpend < budgetLow || targetSpend > budgetHigh) {
        return false;
      }
      return true;
    },
    {
      message:
        "*Your ideal target budget must be in the range of the budget low and high",
      path: ["target_spend"],
    },
  )
  .refine(
    (data) => {
      if (!data.budget_low || !data.budget_low) {
        return false;
      }
      const budgetLow = Number(data.budget_low);
      const budgetHigh = Number(data.budget_high);

      return budgetHigh > budgetLow;
    },
    {
      message: "*Your maximum budget must be greater than your minimum budget",
      path: ["budget_high"],
    },
  );

function isEmptyBudgetValue(val: any) {
  return val === null || val === undefined || val === "";
}

export function ClientSettingForm({
  clientSetting,
  historicalSpendData,
  onExit,
}: {
  clientSetting: ClientSetting | EmptyClientSetting;
  historicalSpendData?: FetchHistoricalMixForCycle;
  onExit?: () => void;
}) {
  const enableMmmReferencePeriod = useShowReferencePeriod();

  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { selectedCycle } = useSelectedCycle();
  const { clientDataColumns: dataColumns } = useClientDataColumns();
  const { kpis } = useClientKpis();
  const {
    kpi: clientSettingKpi,
    desiredKpiValue: clientSettingDesiredKpiValue,
    spendConstraints,
    getSpendConstraintFeature,
    getSpendDataColumns,
  } = useClientSettingData(clientSetting);

  const previousPeriodDateRange = useSelectedCyclePreviousPeriod();

  const {
    upsertClientSetting,
    loading: loadingUpsertClientSetting,
    networkError,
  } = useUpsertClientSetting();

  const availableKpis = useMemo(
    () =>
      orderBy(
        kpis.map((kpi) => ({
          value: String(kpi.id),
          displayValue: kpi.friendly_name,
          ...kpi,
        })),
        "displayValue",
      ),
    [kpis],
  );

  const { initialKpi, initialKpiStringId } = useMemo(() => {
    if (clientSetting.id && clientSettingKpi) {
      return {
        initialKpiStringId: String(clientSettingKpi.id),
        initialKpi: clientSettingKpi,
      };
    } else {
      return {
        initialKpiStringId: String(kpis[0].id),
        initialKpi: kpis[0],
      };
    }
  }, [clientSetting.id, clientSettingKpi, kpis]);

  const [selectedKpi, setSelectedKpi] = useState<FormKpi>({
    type:
      initialKpi.data_columns.length > 1
        ? FormKpiType.COMPLEX
        : FormKpiType.SIMPLE,
    id: String(initialKpi.id),
    name: initialKpi.friendly_name,
    equation: kpis[0].equation,
  });

  const kpi = useMemo(() => {
    return kpis.find((k) => String(k.id) === String(selectedKpi.id));
  }, [kpis, selectedKpi.id]);

  const spendConstraintDataColumns = useMemo(
    () => getSpendDataColumns(dataColumns),
    [dataColumns, getSpendDataColumns],
  );

  const previousPeriodSpendData = useMemo(() => {
    const spendData =
      historicalSpendData?.me.mmm.cycle.historicalMix.data.spend_data;
    let totalSpend = 0;
    const perChannelSpend: Record<string, number> = {};

    (spendData ?? []).forEach((row) => {
      totalSpend += row.spend;
      perChannelSpend[row.data_column_name] = row.spend;
    });

    return { totalSpend, perChannelSpend };
  }, [historicalSpendData]);

  const initialSpendConstraints = spendConstraints
    .map((constraint) => {
      const feature = getSpendConstraintFeature(constraint);
      if (!feature?.id) {
        return null;
      }
      return {
        data_column_id: String(feature.id),
        data_column_name: feature.column_name,
        budget_low:
          constraint.budget_low === null ? null : String(constraint.budget_low),
        budget_high:
          constraint.budget_high === null
            ? null
            : String(constraint.budget_high),
      };
    })
    .filter((constraint) => constraint !== null);

  // NOTE: DS uses an exclusive end date and we need an inclusive end date.
  // The spaghetti fix currently has many edge cases that should be refactored.
  // As of now, a "new" client setting will use the f/e concept of a date range
  // with an inclusive end date, but an existing client setting from the DB will
  // have an exclusive date range. Normalize this with the below variables:
  const { settingStartDate, settingEndDate, settingPeriodLength } =
    useMemo(() => {
      const startDate = clientSetting.client_setting_date_range.start_date;
      if (clientSetting.id) {
        // If this is an existing client setting
        const correctedEndDate = shiftISODate(
          clientSetting.client_setting_date_range.end_date,
          -1,
        );
        return {
          settingStartDate: startDate,
          settingEndDate: correctedEndDate,
          settingPeriodLength: dateRangeLength({
            startDate: startDate,
            endDate: correctedEndDate,
          }),
        };
      }

      return {
        settingStartDate: startDate,
        settingEndDate: clientSetting.client_setting_date_range.end_date,
        settingPeriodLength: dateRangeLength({
          startDate: startDate,
          endDate: clientSetting.client_setting_date_range.end_date,
        }),
      };
    }, [clientSetting.client_setting_date_range, clientSetting.id]);

  const referenceDateRange = useMemo(() => {
    if (
      !clientSetting.reference_start_date &&
      !clientSetting.reference_end_date
    ) {
      return {
        reference_start_date: previousPeriodDateRange.startDate,
        reference_end_date: previousPeriodDateRange.endDate,
      };
    }

    return {
      reference_start_date: clientSetting.reference_start_date,
      reference_end_date: clientSetting.reference_end_date,
    };
  }, [
    clientSetting.reference_start_date,
    clientSetting.reference_end_date,
    previousPeriodDateRange,
  ]);

  const formMeta = useForm<z.infer<typeof ClientSettingSchema>>({
    defaultValues: {
      name: clientSetting.name,
      kpi: initialKpiStringId,
      spend_constraints: initialSpendConstraints ?? [],
      // mix_validations: initialMixValidations ?? [],
      budget_low: String(clientSetting.budget_low),
      budget_high: String(clientSetting.budget_high),
      desired_kpi_value: clientSettingDesiredKpiValue ?? undefined,
      target_spend: String(clientSetting.target_spend),
      max_cost_per_unit_kpi: String(clientSetting.max_cost_per_unit_kpi),
      profit_value_per_unit_kpi: String(
        clientSetting.profit_value_per_unit_kpi
          ? clientSettingKpi?.kpi_type === KpiType.REVENUE
            ? clientSetting.profit_value_per_unit_kpi * 100
            : clientSetting.profit_value_per_unit_kpi
          : "",
      ),
      ...referenceDateRange,
    },
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(ClientSettingSchema),
  });

  const startDate = formMeta.watch("reference_start_date");
  const endDate = formMeta.watch("reference_end_date");
  const isDateRangeUnder2Weeks = useMemo(
    () => moment(endDate).diff(startDate, "days") + 1 < 14,
    [startDate, endDate],
  );

  const handleSubmit = async (values: z.infer<typeof ClientSettingSchema>) => {
    if (isDateRangeUnder2Weeks) {
      return;
    }

    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
      action: "onSubmit",
    });

    const payloadValues = { ...values, kpi: undefined, kpi_v2: undefined };
    const payload = {
      data: {
        ...payloadValues,
        reference_start_date: payloadValues.reference_start_date,
        reference_end_date: payloadValues.reference_end_date,
        profit_value_per_unit_kpi: payloadValues.profit_value_per_unit_kpi
          ? isRevenue
            ? `${parseFloat(payloadValues.profit_value_per_unit_kpi) / 100}`
            : payloadValues.profit_value_per_unit_kpi
          : payloadValues.profit_value_per_unit_kpi,
        cycle_id: selectedCycle.id,
        spend_constraints: payloadValues.spend_constraints.map(
          (constraint) => ({
            ...constraint,
            data_column_v2_id: undefined,
            budget_low: isEmptyBudgetValue(constraint.budget_low)
              ? null
              : constraint.budget_low,
            budget_high: isEmptyBudgetValue(constraint.budget_high)
              ? null
              : constraint.budget_high,
          }),
        ),
        id: clientSetting.id,
        start_date: settingStartDate,
        end_date: settingEndDate,
        kpi_id: values.kpi,
        kpi_v2_id: undefined,
        desired_kpi_value: payloadValues.desired_kpi_value,
      },
    };

    if (!enableMmmReferencePeriod) {
      delete payload.data.reference_start_date;
      delete payload.data.reference_end_date;
    }
    // TODO: Add UI around this validaiton
    // console.log(
    //   validateConstraintsValidationsBudget(
    //     values.spend_constraints,
    //     values.mix_validations,
    //     values.budget_high,
    //     values.budget_low,
    //     dataColumnsMap,
    //   ),
    // );
    const wasSuccessful = await upsertClientSetting(payload);
    if (wasSuccessful) {
      onExit?.();
    }
  };

  // const {
  //   fields: mixValidationFields,
  //   append: appendMixValidationField,
  //   remove: removeMixValidationField,
  // } = useFieldArray({
  //   name: "mix_validations",
  //   control: formMeta.control,
  // });

  const isRevenue = kpi?.kpi_type === KpiType.REVENUE;
  const astTokens = useMemo(
    () =>
      formatKpiEquation(
        selectedKpi.equation,
        (dataColumns as any).filter((d: { is_target: boolean }) => d.is_target),
      ),
    [selectedKpi, dataColumns],
  );

  return (
    <div className="pb-5 text-sm">
      <p className="px-6 text-sm mb-2">
        Your settings determine what mixes we recommend for review.
      </p>
      <form onSubmit={formMeta.handleSubmit(handleSubmit)}>
        <SettingNameField formMeta={formMeta} />
        <SettingKpiField
          formMeta={formMeta}
          availableKpis={availableKpis}
          setSelectedKpi={setSelectedKpi}
          astTokens={astTokens}
          selectedKpi={selectedKpi}
        />
        <SettingBudgetsField
          formMeta={formMeta}
          settingStartDate={settingStartDate}
          settingEndDate={settingEndDate}
          settingPeriodLength={settingPeriodLength}
          previousPeriodSpendData={previousPeriodSpendData}
        />
        <SettingReferencePeriodField
          formMeta={formMeta}
          isDateRangeUnder2Weeks={isDateRangeUnder2Weeks}
        />
        <SettingEfficiencyGoalsField
          formMeta={formMeta}
          isRevenue={isRevenue}
        />
        <SettingSpendRequirementsField
          formMeta={formMeta}
          spendConstraintDataColumns={spendConstraintDataColumns}
          previousPeriodSpendData={previousPeriodSpendData}
          dataColumns={dataColumns}
          settingPeriodLength={settingPeriodLength}
          spendConstraints={spendConstraints}
        />
        {/* <div className="mt-0 border-b border-v2-secondaryp-6 bg-cosmic-4">
          <label className="block font-semibold">Budget Validation</label>
          {mixValidationFields.length > 0 && (
            <div className="mt-4 mb-4 flex justify-end gap-[114px] mr-[22px]">
              <span className="uppercase text-xs">Min. % of budget</span>
              <span className="uppercase text-xs">Max. % of budget</span>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {mixValidationFields.map((mixValidationField, index) => (
              <MixValidationFormComponent
                key={mixValidationField.id}
                mixValidationField={mixValidationField}
                remove={removeMixValidationField}
                form={formMeta}
                dataColumnsMap={dataColumnsMap}
                index={index}
                periodLengthDays={settingPeriodLength}
                minBudgetValue={currentMixValidations[index].percentage_low}
                maxBudgetValue={currentMixValidations[index].percentage_high}
                historicalPercentage={getHistoricalSpendPercentageFromDataColumnId(
                  String(mixValidationField.data_column_id),
                )}
                hasError={!!mixValidationsErrors?.[index]}
              />
            ))}
          </div>
          <AppendMixValidationButton
            append={appendMixValidationField}
            availableDataColumns={spendConstraintDataColums}
            selectedDataColumnsIds={currentMixValidations.map(
              (c) => c.data_column_id,
            )}
          />
        </div> */}
        <div>
          <div className="flex justify-end items-end gap-2 px-6 pt-5">
            <TertiaryButton onClick={onExit}>CANCEL</TertiaryButton>
            <PrimaryButton loading={loadingUpsertClientSetting} type="submit">
              SAVE
            </PrimaryButton>
          </div>
          {networkError && (
            <span className="text-right text-sm text-mars font-semibold mr-4 mt-2 pb-2 block">
              A problem occurred. Please try again later!
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
